
'use client';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAppStore } from '@/lib/data';
import { formatCurrency } from '@/lib/utils';
import type { Quote } from '@/lib/types';
import { MoreHorizontal, View, Trash2, Download } from 'lucide-react';
import { Link } from 'react-router-dom';
import { EditQuoteSheet } from './edit-quote-sheet';
import { useToast } from '@/hooks/use-toast';
import { kwachaAI, downloadBlob } from '@/lib/kwacha-ai-client';
import { useAuth } from '@/hooks/use-auth';

export function QuotesDataTable({ data }: { data: Quote[] }) {
    const { clients, deleteQuote } = useAppStore();
    const { toast } = useToast();
    const { user } = useAuth();
    
    const getClientName = (clientId: string) => {
        return clients.find(c => c.id === clientId)?.name || 'Unknown Client';
    }

    const getTotalAmount = (items: Quote['items']) => {
        return items.reduce((total, item) => total + (item.price * item.quantity), 0);
    }

    const handleDelete = (quote: Quote) => {
        deleteQuote(quote.id);
        toast({
            title: 'Quote Deleted',
            description: `Quote ${quote.quoteNumber} has been removed.`,
            variant: 'destructive',
        });
    };

    const handleDownloadPDF = async (quote: Quote) => {
        if (!user?.id) return;
        
        try {
            toast({
                title: 'Generating PDF',
                description: 'Please wait while we prepare your document...',
            });
            
            const blob = await kwachaAI.downloadQuotePDF(quote.id, user.id);
            downloadBlob(blob, `quote_${quote.quoteNumber}.pdf`);
            
            toast({
                title: 'Download Started',
                description: `Quote ${quote.quoteNumber} is being downloaded.`,
            });
        } catch (error) {
            console.error('PDF Download Error:', error);
            toast({
                title: 'Download Failed',
                description: 'Failed to generate PDF. Please try again later.',
                variant: 'destructive',
            });
        }
    };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Quote #</TableHead>
          <TableHead>Client</TableHead>
          <TableHead>Date</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="text-right">Amount</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.map((quote) => {
          return (
            <TableRow key={quote.id} className="row-hover-minimal">
              <TableCell>
                <div className="font-medium">{quote.quoteNumber}</div>
              </TableCell>
              <TableCell>
                {getClientName(quote.clientId)}
              </TableCell>
              <TableCell>
                {new Date(quote.date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </TableCell>
               <TableCell>
                <span className={`${quote.status === 'accepted' ? 'badge-success' : (quote.status === 'sent' ? 'badge-info' : '')} px-2 py-0.5 rounded-md text-xs capitalize`}>
                    {quote.status}
                </span>
              </TableCell>
              <TableCell
                className="text-right font-medium"
              >
                {formatCurrency(getTotalAmount(quote.items))}
              </TableCell>
              <TableCell className="text-right">
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0">
                            <span className="sr-only">Open menu</span>
                            <MoreHorizontal className="h-4 w-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <DropdownMenuLabel>Actions</DropdownMenuLabel>
                        <DropdownMenuItem asChild>
                           <Link to={`/dashboard/quotes/${quote.id}`}>
                             <View className="mr-2 h-4 w-4" />
                             View Quote
                           </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                            <EditQuoteSheet quote={quote} />
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDownloadPDF(quote)}>
                            <Download className="mr-2 h-4 w-4" />
                            Download PDF
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem className="text-destructive" onClick={() => handleDelete(quote)}>
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete Quote
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
